/* Collating duplicate drawings on an order.  Charl, 20 Sep 2026.

   Maryke draws every flashing off the paper pages, so an order arrives with the same
   profile drawn five times over with one leg different each time.  This finds those and
   folds them into ONE drawing with a table of lengths under it, using the X / X1 feature
   the printed sheet already draws.

   HIS RULES, given 20 Sep and checked against order FF-711177B by hand:
     - at most TWO leg measurements may vary across the whole group -> X and X1
     - same number of folds, drawn the same way
     - every angle identical.  An angle difference is a different flashing, full stop
     - same crush folds and hems.  One has a crush fold and the other does not -> apart
     - same material, colour and thickness.  A different colour is a different flashing

   WHAT DELIBERATELY DOES *NOT* SPLIT A GROUP:
     colour SIDE (outside vs inside).  Charl's own grouping put #1 (outside) with #4
     (inside), so the side travels with the ROW, not the drawing, and every row prints
     its side.  Flip SPLIT_ON_COLOUR_SIDE to true if that is ever wrong. */

export const MAX_X = 2;                  /* his cap - two X measurements, no more */
/* No angle tolerance.  angAt() ROUNDS to whole degrees, and that rounded number is what
   he reads off the drawing, so two corners group exactly when the sheet shows the same
   number.  Anything looser would group two flashings his own sheet says are different. */
export const LEG_TOL = 0.5;              /* mm */
export const SPLIT_ON_COLOUR_SIDE = false;

/* The angle at a corner, in degrees.  This MUST agree with angAt() in DrawingApp.js,
   which is what puts the degree labels on the drawing and the printed sheet - a
   grouping that disagreed with the numbers he can see would be worse than useless.
   tests/grouping.test.js proves the two agree across random shapes. */
export function cornerAngle(p, c, n) {
  const v1 = { x: p.x - c.x, y: p.y - c.y };
  const v2 = { x: n.x - c.x, y: n.y - c.y };
  const dot = v1.x * v2.x + v1.y * v2.y;
  const mag = Math.sqrt(v1.x ** 2 + v1.y ** 2) * Math.sqrt(v2.x ** 2 + v2.y ** 2);
  if (mag < 0.001) return 90;                       /* same fallback as angAt */
  return Math.round(Math.acos(Math.max(-1, Math.min(1, dot / mag))) * 180 / Math.PI);
}

/* ⚠️ Charl, 20 Sep, off the collated sheet: "you have swapped the girth and X
   measurements because the X can't be bigger than the flashing girth".  Not swapped -
   DOUBLED.  pts are stored in PIXELS, and the drawing is 2 pixels to the millimetre.
   This measured raw pixels, so every X printed at exactly twice its true size: a 140mm
   leg read 280mm.  The app measures a leg with sLen(), which divides by PX first, and
   that is the only way it should ever be done here.  I reused angAt for the angles and
   then wrote my own for the lengths - the lengths are what it cost.
   PX and the rounding match DrawingApp.js exactly; a guard test proves they agree. */
export const PX = 2.0;
const pxMm = px => +(px / PX).toFixed(1);

/* leg lengths in MILLIMETRES, in the order they are drawn */
export function legsOf(item) {
  const pts = (item && item.pts) || [];
  const out = [];
  for (let i = 0; i < pts.length - 1; i++) {
    out.push(Math.sqrt(pxMm(pts[i + 1].x - pts[i].x) ** 2 + pxMm(pts[i + 1].y - pts[i].y) ** 2));
  }
  return out;
}

export function anglesOf(item) {
  const pts = (item && item.pts) || [];
  const out = [];
  for (let i = 1; i < pts.length - 1; i++) out.push(cornerAngle(pts[i - 1], pts[i], pts[i + 1]));
  return out;
}

/* Everything that must match before two drawings may even be compared leg by leg.
   If any of this differs they are different flashings and that is the end of it. */
export function signatureOf(item) {
  const h = (item && item.hems) || {};
  const a = anglesOf(item);                          /* already whole degrees */
  return JSON.stringify({
    folds: legsOf(item).length,
    angles: a,
    material: (item && (item.matId || item.material)) || '',
    thickness: (item && item.thickness) || '',
    colour: (item && item.colour) || '',
    side: SPLIT_ON_COLOUR_SIDE ? ((item && item.colourFace) || '') : '',
    /* a crush fold, a hem, its size and which way it folds - all part of the shape */
    hemStart: h.start || 'none', hemEnd: h.end || 'none',
    hemStartMm: h.startFoldMm === undefined ? '' : h.startFoldMm,
    hemEndMm: h.endFoldMm === undefined ? '' : h.endFoldMm,
    hemStartFlip: h.startFlip === undefined ? '' : h.startFlip,
    hemEndFlip: h.endFlip === undefined ? '' : h.endFlip,
    markStart: (item && item.startMarker) || 'none',
    markEnd: (item && item.endMarker) || 'none',
    rounded: !!(item && item.roundCorners),
    taper: !!(item && item.taperLegs)
  });
}

/* which leg positions differ across a set of drawings */
function varyingPositions(items) {
  const legs = items.map(legsOf);
  const n = legs[0].length;
  const out = [];
  for (let i = 0; i < n; i++) {
    const v = legs.map(L => L[i]);
    if (Math.max.apply(null, v) - Math.min.apply(null, v) > LEG_TOL) out.push(i);
  }
  return out;
}

/* An item already drawn WITH an X is left alone: its legs are not fixed numbers, so
   comparing them would be meaningless.  Better to skip it and say so than to guess. */
export function alreadyHasX(item) {
  return !!(item && item.xSegs && item.xSegs.length);
}

/* The grouping itself.  Greedy: seed a group, then add a drawing only if the legs that
   vary across the WHOLE group still number MAX_X or fewer.  The union matters, not the
   pairwise difference - three drawings each differing from the next by one leg can still
   need three X values between them, which is over the cap. */
export function groupItems(items) {
  const list = (items || []).map((it, i) => ({ it: it, no: i + 1 }));
  const skipped = list.filter(e => alreadyHasX(e.it));
  const usable = list.filter(e => !alreadyHasX(e.it));

  const buckets = {};
  usable.forEach(e => {
    const k = signatureOf(e.it);
    (buckets[k] = buckets[k] || []).push(e);
  });

  const groups = [];
  Object.keys(buckets).forEach(k => {
    const bucket = buckets[k];
    const open = [];
    bucket.forEach(e => {
      let placed = false;
      for (let g = 0; g < open.length && !placed; g++) {
        const trial = open[g].concat([e]);
        if (varyingPositions(trial.map(x => x.it)).length <= MAX_X) { open[g] = trial; placed = true; }
      }
      if (!placed) open.push([e]);
    });
    open.forEach(g => groups.push(g));
  });

  return groups.map(g => {
    const xs = varyingPositions(g.map(e => e.it));
    return {
      drawings: g.map(e => e.no),
      count: g.length,
      xPositions: xs,
      /* one row per source drawing, carrying its own X values, its lengths and its side */
      rows: g.map((e, ri) => ({
        wasDrawing: e.no,
        /* ⚠️ Charl, 20 Sep: "as they do the cutting they number the flashings so that they
           know which drawing it belongs to for folding". Once seven drawings become one,
           the number alone no longer says which. The MARK does - A, B, C - and the printed
           X table already carries that column, so the piece is marked "1A", "1B". */
        mark: String.fromCharCode(65 + ri),
        label: e.it.presetLabel || 'Custom Flashing',
        colourFace: e.it.colourFace || null,
        xValues: xs.map(i => Math.round(legsOf(e.it)[i])),
        lengths: (e.it.lengths && e.it.lengths.length)
          ? e.it.lengths.map(L => ({ len: L.len, qty: L.qty }))
          : [{ len: e.it.length, qty: e.it.qty }]
      }))
    };
  /* Ordered by the lowest drawing number in each group, so #1 stays #1 and the
     renumbering is as close as possible to what Maryke drew. */
  }).sort((a, b) => a.drawings[0] - b.drawings[0])
    .concat(skipped.map(e => ({
      drawings: [e.no], count: 1, xPositions: [], alreadyHasX: true,
      rows: [{ wasDrawing: e.no, label: e.it.presetLabel || 'Custom Flashing',
               colourFace: e.it.colourFace || null, xValues: [], lengths: [] }]
    })));
}

/* a one-line summary for the screen */
export function groupSummary(items) {
  const g = groupItems(items);
  /* the number the drawing WOULD carry after collating, so the mark on the steel
     ("2B") can be read off the report before anything is applied */
  g.forEach((grp, i) => {
    grp.newNumber = i + 1;
    grp.rows.forEach(r => { r.pieceMark = (i + 1) + (grp.count > 1 ? r.mark : ''); });
  });
  const merged = g.filter(x => x.count > 1);
  return {
    before: (items || []).length,
    after: g.length,
    mergedGroups: merged.length,
    saved: (items || []).length - g.length,
    groups: g
  };
}

/* Turn the report into the actual items.  ONE drawing per group, with a length row for
   every length of every drawing that went into it, each row carrying its own mark, its
   own X values and its own girth.

   ⚠️ Charl, 20 Sep: "technically nothing changes on the cutting lists, it's only
   numbering that changes".  That is exactly right and it is the invariant the server
   checks before it will save: the steel - girth, length, quantity, material, colour,
   thickness - must come out identical.  If it does not, something is wrong and the
   collate is refused rather than written. */
export function applyGrouping(items) {
  const sum = groupSummary(items);
  const out = sum.groups.map(g => {
    const first = items[g.drawings[0] - 1];
    const rows = [];
    g.rows.forEach(r => {
      const src = items[r.wasDrawing - 1];
      const xVals = {};
      g.xPositions.forEach((p, k) => { xVals[p] = r.xValues[k]; });
      const srcLens = (src.lengths && src.lengths.length)
        ? src.lengths
        : [{ len: src.length, qty: src.qty }];
      srcLens.forEach(L => {
        rows.push(Object.assign({}, L, {
          /* what the cutter writes on the steel.  A group of one keeps a plain number. */
          mark: g.count > 1 ? r.mark : '',
          xVals: g.count > 1 ? xVals : (L.xVals || undefined),
          girth: L.girth !== undefined ? L.girth
            : Math.round((src.stats && (src.stats.totalWithHems || src.stats.totalMm)) || 0),
          /* the colour side travels with the ROW - his grouping puts outside and inside
             together, so the floor reads the side off the line it is working */
          colourFace: src.colourFace || null,
          fromDrawing: r.wasDrawing,
          rowLabel: src.presetLabel || 'Custom Flashing'
        }));
      });
    });
    return Object.assign({}, first, {
      lengths: rows,
      xSegs: g.count > 1 ? g.xPositions.slice() : (first.xSegs || null),
      qty: rows.reduce((t, r) => t + (Number(r.qty) || 0), 0),
      collatedFrom: g.count > 1 ? g.drawings.slice() : null,
      /* ⚠️ a drawing that was NOT collated still needs to know which number it was, or
         un-collating cannot put it back in its place. Charl, 20 Sep. */
      wasDrawing: g.count > 1 ? null : g.drawings[0]
    });
  });
  return out;
}

/* The steel, as a comparable fingerprint.  Used on BOTH sides: the client will not offer
   a collate that changes it and the server will not save one. */
export function steelFingerprint(items) {
  const rows = [];
  (items || []).forEach(it => {
    const g = Math.round((it.stats && (it.stats.totalWithHems || it.stats.totalMm)) || 0);
    const lens = (it.lengths && it.lengths.length) ? it.lengths : [{ len: it.length, qty: it.qty }];
    lens.forEach(L => rows.push([
      L.girth !== undefined ? L.girth : g,
      Number(L.len) || 0, Number(L.qty) || 0,
      it.matId || it.material || '', it.colour || '', it.thickness || ''
    ].join('|')));
  });
  return rows.sort().join('\n');
}

/* ---- Un-collating -------------------------------------------------------------
   Charl, 20 Sep: the collate on FF-711177B ran while legs were being measured in raw
   pixels, so every X was written into the order at exactly twice its true size. The
   girths were right. The code is fixed now, but the wrong numbers are sitting in the
   saved order and no deploy touches them - so the order has to be taken apart and done
   again.

   It can be, because each row kept `fromDrawing` and `rowLabel`. Each mark becomes its
   flashing again, with its own name, its own number and its own lengths.

   The X values are REPAIRED on the way out, not trusted: everything that is not an X
   leg is measured off the geometry (which was never wrong - pts are honest pixels), and
   the X legs must account for exactly what is left of the girth. If the stored values
   do not scale cleanly onto that, the row is refused rather than guessed at. */

/* the girth a drawing would have with no X legs at all: the fixed legs plus the folds */
export function fixedGirthOf(item) {
  const xs = (item.xSegs || []);
  const legs = legsOf(item);
  let t = 0;
  legs.forEach((L, i) => { if (xs.indexOf(i) < 0) t += L; });
  const h = item.hems || {};
  if (h.start === 'crush') t += (h.startFoldMm || 0);
  if (h.end === 'crush') t += (h.endFoldMm || 0);
  return Math.round(t);
}

/* What the X values on one length row SHOULD be.

   ⚠️ Charl, 21 Sep: "there is still no X and no X1 when I collate." Traced it, and
   this function was the cause. It used to demand that the STORED X values scale onto
   the girth by a clean 1.0 or 0.5, and refuse otherwise. When it refused, the un-collate
   left the geometry untouched - so every drawing recovered from a group came out with
   IDENTICAL legs, and re-collating them then found nothing that varied. Result: xSegs
   empty, no X, no X1, no table. A silent refusal that produced a worse order than doing
   nothing at all.

   The girth is the authority and it was never wrong. With ONE X leg no arithmetic is
   needed: X is whatever the girth has left over. Only splitting between two or more X
   legs needs the stored values, and then only for their proportions. */
export function repairedXs(item, row) {
  const xs = (item.xSegs || []);
  if (!xs.length) return {};
  const girth = Number(row.girth) || 0;
  if (!girth) return null;
  const want = girth - fixedGirthOf(item);
  if (want <= 0) return null;

  /* one X: exact, and it cannot fail */
  if (xs.length === 1) { const o = {}; o[xs[0]] = want; return o; }

  /* two or more: keep the proportions they were stored in, scaled to fit the girth.
     If they are unusable, fall back to an even split rather than refusing - a drawing
     with the right girth and an even split is still correct steel and still shows an X;
     refusing produced duplicate drawings with no X at all. */
  const stored = xs.map(si => Number((row.xVals || {})[si]) || 0);
  const sum = stored.reduce((a, b) => a + b, 0);
  const out = {};
  let acc = 0;
  xs.forEach((si, k) => {
    const share = sum > 0 ? (stored[k] / sum) : (1 / xs.length);
    const v = (k === xs.length - 1) ? (want - acc) : Math.round(want * share);
    out[si] = v; acc += v;
  });
  if (Object.keys(out).some(k => out[k] <= 0)) return null;
  return out;
}

/* Move one leg of a polyline to a new length, carrying everything after it along. */
function setLegLength(pts, idx, mmLen) {
  const p = pts.map(q => ({ x: q.x, y: q.y }));
  const a = p[idx], b = p[idx + 1];
  const dx = b.x - a.x, dy = b.y - a.y;
  const l = Math.hypot(dx, dy) || 1;
  const want = mmLen * PX;
  const sx = a.x + (dx / l) * want, sy = a.y + (dy / l) * want;
  const shiftX = sx - b.x, shiftY = sy - b.y;
  for (let i = idx + 1; i < p.length; i++) { p[i].x += shiftX; p[i].y += shiftY; }
  return p;
}

/* Take a collated order back to the flashings it was made from. */
/* ⚠️ Charl, 21 Sep: the undo did not appear on FF-711177B at all. It keyed off
   `collatedFrom`, which that order's saved drawings do not carry. Never depend on one
   field for this. A collated drawing shows itself three independent ways, and ANY of
   them is enough:
     - collatedFrom, the list of drawings it was made from
     - length rows carrying a MARK (A, B, C) - the thing written on the steel
     - more than one row pointing at a different original drawing
   An order that has been collated has all three; one that lost a field still gets
   its way back. */
export function looksCollated(item) {
  if (!item) return false;
  if (item.collatedFrom && item.collatedFrom.length) return true;
  const rows = item.lengths || [];
  if (rows.some(r => r && r.mark)) return true;
  const from = rows.map(r => r && r.fromDrawing).filter(Boolean);
  return new Set(from).size > 1;
}

export function anyCollated(items) {
  return (items || []).some(looksCollated);
}

export function uncollateItems(items) {
  const out = [];
  (items || []).forEach(item => {
    if (!looksCollated(item)) { out.push(item); return; }
    const xs = (item.xSegs || []);
    const byMark = {};
    (item.lengths || []).forEach(L => {
      const k = L.mark || String(L.fromDrawing || '');
      (byMark[k] = byMark[k] || []).push(L);
    });
    Object.keys(byMark).forEach(k => {
      const rows = byMark[k], first = rows[0];
      const fixedXs = repairedXs(item, first);
      let pts = item.pts;
      if (fixedXs) xs.forEach(si => { pts = setLegLength(pts, si, fixedXs[si]); });
      /* ⚠️ if it could not be worked out, say so on the drawing rather than quietly
         handing back several identical ones - that is what produced an order with no
         X anywhere. */
      out.push(Object.assign({}, item, {
        pts: pts,
        xSegs: null,
        collatedFrom: null,
        presetLabel: first.rowLabel || item.presetLabel,
        colourFace: first.colourFace || item.colourFace,
        _wasDrawing: first.fromDrawing || null,
        _repaired: !!fixedXs,
        _unrepairable: !fixedXs,
        qty: rows.reduce((t, r) => t + (Number(r.qty) || 0), 0),
        lengths: rows.map(r => ({ len: r.len, qty: r.qty, girth: r.girth })),
        stats: Object.assign({}, item.stats, {
          totalMm: first.girth, totalWithHems: first.girth
        })
      }));
    });
  });
  /* Back into the order they were drawn in.

     A drawing that was never collated may carry no number - orders collated before
     20 Sep did not record one for them. But every number from 1 to N must be accounted
     for, so the gaps left by the numbered ones are exactly the missing drawings, and
     the un-numbered ones slot into those gaps in the order they appear. That is exact,
     not a guess, and it is checked: if the gaps and the un-numbered count do not match,
     the numbering is left alone rather than invented. */
  out.forEach(o => { if (!o._wasDrawing && o.wasDrawing) o._wasDrawing = o.wasDrawing; });
  const known = out.filter(o => o._wasDrawing).map(o => o._wasDrawing);
  const blank = out.filter(o => !o._wasDrawing);
  if (blank.length) {
    const gaps = [];
    for (let n = 1; n <= out.length; n++) if (known.indexOf(n) < 0) gaps.push(n);
    if (gaps.length === blank.length) blank.forEach((o, i) => { o._wasDrawing = gaps[i]; });
  }
  return out.sort((a, b) => (a._wasDrawing || 1e9) - (b._wasDrawing || 1e9));
}

/* ⚠️ Charl, 21 Sep: "if I collate there is no X X1 with table below the drawings."
   The drawing shows X, and the table that says what X IS only ever existed on the
   printed sheet - so on the Factory Board he got a letter with nothing to read it
   against. One helper, so the board and the sheet cannot drift apart. */
export function variableRows(item) {
  const xs = (item && item.xSegs && item.xSegs.length) ? item.xSegs : [];
  if (!xs.length) return [];
  return (item.lengths || []).map((L, n) => ({
    mark: L.mark || String.fromCharCode(65 + n),
    wasDrawing: L.fromDrawing || null,
    label: L.rowLabel || '',
    len: L.len,
    qty: L.qty,
    girth: L.girth,
    colourFace: L.colourFace || null,
    xValues: xs.map((si, k) => ({
      name: k === 0 ? 'X' : 'X' + k,
      value: (L.xVals || {})[si]
    }))
  }));
}

/* ---- Recovering an order that lost its X ---------------------------------------
   ⚠️ Charl, 21 Sep, off the printed sheet: drawing #1 read "55mm 140mm 25mm" as fixed
   numbers while its eight rows carried eight different girths. xSegs was EMPTY on the
   saved order. That state is self-perpetuating: un-collating gives seven identical
   drawings, collating those finds nothing that varies, and the X never comes back.

   The girths survived, and for a single X leg they are enough. The X leg is the one
   where EVERY row's implied length (girth minus the other legs and the folds) is still
   a leg a machine could fold. On his order exactly one leg qualifies on each of #1 and
   #3; the other legs imply 5mm and -20mm legs, which cannot be.

   Two X legs cannot be recovered and this does not try: the girths say only what the
   two legs SUM to, not how they split - 240mm is 100+140 or 111+129 and nothing here
   can tell them apart. Those are returned untouched and flagged, never guessed. */

export const MIN_REAL_LEG = 20;   /* mm - below this it is not a leg anyone folds */

export function inferXSeg(item, minLeg) {
  const rows = (item && item.lengths) || [];
  if (rows.length < 2) return null;
  const girths = rows.map(r => Number(r.girth) || 0);
  if (girths.some(g => !g)) return null;
  /* nothing to infer unless the rows actually differ */
  if (Math.max.apply(null, girths) - Math.min.apply(null, girths) < 0.5) return null;

  const legs = legsOf(item);
  if (legs.length < 2) return null;
  const h = item.hems || {};
  let folds = 0;
  if (h.start === 'crush') folds += (h.startFoldMm || 0);
  if (h.end === 'crush') folds += (h.endFoldMm || 0);
  const floor = (minLeg === undefined ? MIN_REAL_LEG : minLeg);

  const ok = [];
  legs.forEach((L, i) => {
    const fixed = legs.reduce((t, v, k) => t + (k === i ? 0 : v), 0) + folds;
    const implied = girths.map(g => g - fixed);
    if (Math.min.apply(null, implied) >= floor) ok.push(i);
  });
  /* only when it is unambiguous - one leg and one leg only */
  return ok.length === 1 ? ok[0] : null;
}

/* Put the X back on a collated drawing that lost it. Returns the item unchanged when
   it cannot be done, with `_lostX` set so the screen can say so rather than pretend. */
/* ⚠️ Charl, 21 Sep: "I don't understand, all the measurements are there." He was
   right and I had written off drawing #2 too quickly.

   Even when xSegs was wiped, the length ROWS may still carry their old xVals. Those
   numbers were wrong - written at twice size - but their PROPORTIONS were not, and the
   girth says what they must add up to. So a two-X drawing recovers exactly:

       girth 340, stored 200/280  ->  100/140, which is the original

   Better still, the xVals KEYS name which legs were the X, so nothing has to be
   inferred at all. This is tried FIRST, and the single-leg inference is only the
   fallback for rows that kept nothing. */
export function xsFromStoredRows(item) {
  const rows = (item && item.lengths) || [];
  const keyed = rows.filter(r => r && r.xVals && Object.keys(r.xVals).length);
  if (!keyed.length) return null;
  const keys = Object.keys(keyed[0].xVals).map(Number).sort((a, b) => a - b);
  if (!keys.length) return null;
  /* every row that has values must name the same legs */
  const same = keyed.every(r => {
    const k = Object.keys(r.xVals).map(Number).sort((a, b) => a - b);
    return k.length === keys.length && k.every((v, i) => v === keys[i]);
  });
  if (!same) return null;
  const legs = legsOf(item);
  if (keys.some(k => k < 0 || k >= legs.length)) return null;
  return keys;
}

export function restoreLostX(item, minLeg) {
  if (!item || (item.xSegs && item.xSegs.length)) return item;
  if (!looksCollated(item)) return item;

  /* the rows' own stored values, if they survived - exact, and works for any number
     of X legs including two */
  const fromRows = xsFromStoredRows(item);
  if (fromRows) {
    const legs = legsOf(item);
    const h = item.hems || {};
    let folds = 0;
    if (h.start === 'crush') folds += (h.startFoldMm || 0);
    if (h.end === 'crush') folds += (h.endFoldMm || 0);
    const fixed = legs.reduce((t, v, k) => t + (fromRows.indexOf(k) >= 0 ? 0 : v), 0) + folds;
    let bad = false;
    const out = (item.lengths || []).map(L => {
      const want = (Number(L.girth) || 0) - fixed;
      const stored = fromRows.map(k => Number((L.xVals || {})[k]) || 0);
      const sum = stored.reduce((a, b) => a + b, 0);
      if (want <= 0 || sum <= 0) { bad = true; return L; }
      const xv = {}; let acc = 0;
      fromRows.forEach((k, i) => {
        const v = (i === fromRows.length - 1) ? (want - acc) : Math.round(want * stored[i] / sum);
        xv[k] = v; acc += v;
      });
      if (fromRows.some(k => xv[k] <= 0)) bad = true;
      return Object.assign({}, L, { xVals: xv });
    });
    if (!bad) return Object.assign({}, item, { xSegs: fromRows.slice(), _lostX: false, lengths: out });
  }

  const si = inferXSeg(item, minLeg);
  if (si === null) return Object.assign({}, item, { _lostX: true });
  const legs = legsOf(item);
  const h = item.hems || {};
  let folds = 0;
  if (h.start === 'crush') folds += (h.startFoldMm || 0);
  if (h.end === 'crush') folds += (h.endFoldMm || 0);
  const fixed = legs.reduce((t, v, k) => t + (k === si ? 0 : v), 0) + folds;
  return Object.assign({}, item, {
    xSegs: [si],
    _lostX: false,
    lengths: (item.lengths || []).map(L => {
      const xv = {};
      xv[si] = Math.round((Number(L.girth) || 0) - fixed);
      return Object.assign({}, L, { xVals: xv });
    })
  });
}

export function restoreLostXAll(items, minLeg) {
  return (items || []).map(it => restoreLostX(it, minLeg));
}

/* how many drawings on an order lost their X, and how many can be put back */
export function lostXReport(items, minLeg) {
  const lost = (items || []).filter(it =>
    looksCollated(it) && !(it.xSegs && it.xSegs.length));
  const fixable = lost.filter(it =>
    xsFromStoredRows(it) !== null || inferXSeg(it, minLeg) !== null);
  return { lost: lost.length, fixable: fixable.length,
           stuck: lost.length - fixable.length };
}
